import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import posthog from "posthog-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, type SubmitErrorHandler, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import SearchableSelect from "../../../components/SearchableSelect";
import { Button } from "../../../components/ui/button";
import { Field, FieldError, FieldLabel } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";
import { getErrorMessage } from "../../../handlers/errorHandling";
import { months, monthToNumber } from "../../../handlers/utilityFunctions";
import { getJobFunctions } from "../../../seed/getMetadata";
import { seniorityLevels } from "../../../seed/job-preferences-data";
import mapping from "../../../seed/metadata/filemapping";
import { API } from "../../../services/apiService";
import { primeUserAccount, useUserAccount } from "../../../services/create-resume/queries";
import { ResumeDataService } from "../../../services/create-resume/resume-data";
import FileUpload from "../../Profile/components/FileUpload";
import OptionBadge from "../components/OptionBadge";
import AuthLayout from "../layout/AuthLayout";
import {
	clearPendingSignup,
	getPendingSignup,
	type PendingSignup,
	type ResumeSetupState,
	setPendingSignup,
	type YesNo,
} from "../pendingSignup";
import { deriveSeniorityLevel } from "../seniority";
import ExtractionWaiting from "./ExtractionWaiting";
import { reconcileResumeSetup, useResumeSetup } from "./useResumeSetup";

const MAX_JOB_TITLE_LENGTH = 100;

const onboardingSchema = z
	.object({
		firstName: z.string().trim().min(1, "Please enter your first and last name"),
		lastName: z.string().trim().min(1, "Please enter your first and last name"),
		jobTitle: z.string().trim().min(1, "Please enter your desired job title"),
		areasOfExpertise: z.array(z.string()).min(1, "Please select your area of expertise"),
		recentGraduate: z
			.string()
			.refine((value) => value === "Yes" || value === "No", "Please let us know if you are a recent graduate"),
		experienceMonth: z.string(),
		experienceYear: z.string(),
		seniorityLevel: z.string().min(1, "Please tell us how much experience you have"),
		location: z.string().min(1, "Please select where you are located"),
	})
	.superRefine((values, context) => {
		if (values.experienceMonth && values.experienceYear) return;

		context.addIssue({
			code: "custom",
			path: ["experienceMonth"],
			message:
				values.recentGraduate === "Yes"
					? "Please select when you graduated or expect to graduate"
					: "Please select when you started your professional career",
		});
	});

type OnboardingFormInput = z.input<typeof onboardingSchema>;
type OnboardingFormValues = z.output<typeof onboardingSchema>;

/**
 * Reading order, so a failed submit takes the user to the first thing they missed rather than
 * leaving them to hunt for red text below the fold — which on a phone is most of the form.
 */
const FIELDS_IN_ORDER = [
	{ name: "firstName", elementId: "onboarding-first-name" },
	{ name: "lastName", elementId: "onboarding-last-name" },
	{ name: "jobTitle", elementId: "onboarding-job-title" },
	{ name: "areasOfExpertise", elementId: "onboarding-areas-of-expertise" },
	{ name: "recentGraduate", elementId: "onboarding-recent-graduate" },
	{ name: "experienceMonth", elementId: "onboarding-experience-month" },
	{ name: "seniorityLevel", elementId: "onboarding-seniority" },
	{ name: "location", elementId: "onboarding-location" },
] as const satisfies ReadonlyArray<{ name: keyof OnboardingFormInput; elementId: string }>;

/** Matches Tailwind's `md`, where AuthLayout stops stacking and the form fits on one screen. */
const isMobileViewport = () => window.matchMedia("(max-width: 767px)").matches;

const PDF_ONLY = [{ name: "PDF", extension: ".pdf", mimeType: "application/pdf" }];

/** "District,Region" pairs — the same controlled vocabulary the resume builder's address field uses. */
const LOCATIONS: string[] = mapping["districtsRegions"][0].districtRegions;

const SENIORITY_OPTIONS = seniorityLevels.map((level) => level.value);

/** The stored value is "District,Region"; the API wants them apart. */
function splitLocation(location: string) {
	const [city = "", region = ""] = location.split(",").map((part) => part.trim());
	return { city, region };
}

/**
 * One easing for every open/close on this screen, so nothing feels out of step. Slow enough to
 * read as one block making room for another, and eased so it settles rather than stops.
 */
const COLLAPSE = { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const };

/** Graduating soon counts too, so the window runs a couple of years into the future. */
function graduationYears(): string[] {
	const thisYear = new Date().getFullYear();
	const years = [];
	for (let year = thisYear - 3; year <= thisYear + 2; year++) years.push(String(year));
	return years.reverse();
}

function careerStartYears(): string[] {
	const thisYear = new Date().getFullYear();
	const years = [];
	for (let year = 1980; year <= thisYear; year++) years.push(String(year));
	return years.reverse();
}

function OnboardingScreen() {
	const navigate = useNavigate();
	const pendingSignup = useMemo(() => getPendingSignup(), []);
	const api = useMemo(() => new ResumeDataService(), []);
	const jobFunctionOptions = useMemo(() => [...getJobFunctions()].sort(), []);
	const {
		clearErrors,
		control,
		formState: { errors, isDirty },
		handleSubmit: submitForm,
		register,
		reset,
		setValue,
		watch,
	} = useForm<OnboardingFormInput, unknown, OnboardingFormValues>({
		resolver: zodResolver(onboardingSchema),
		defaultValues: {
			firstName: pendingSignup?.firstName || "",
			lastName: pendingSignup?.lastName || "",
			jobTitle: pendingSignup?.jobTitle || "",
			areasOfExpertise: pendingSignup?.areasOfExpertise || [],
			recentGraduate: pendingSignup?.recentGraduate || "",
			experienceMonth: pendingSignup?.experienceMonth || "",
			experienceYear: pendingSignup?.experienceYear || "",
			seniorityLevel: pendingSignup?.seniorityLevel || "",
			location: pendingSignup?.location || "",
		},
	});
	const recentGraduate = watch("recentGraduate");
	const [submitting, setSubmitting] = useState(false);
	// Synchronous guard: `submitting` state only disables the button on the next
	// render, so a fast double-submit could fire two POSTs before that lands.
	const isCreatingRef = useRef(false);

	// A draft rebuilt at sign-in for an account whose record went missing carries no name — Cognito
	// only ever stores the email address. POST /users requires both, so ask for them here rather than
	// sending the user through a second screen. Derived from the stored draft, not from state, so the
	// fields don't vanish while they're being filled in.
	const needsName = Boolean(pendingSignup) && !(pendingSignup.firstName?.trim() && pendingSignup.lastName?.trim());

	const answeredQuestions = useRef<Set<string>>(new Set());

	// If a signed-in user reaches this page with a stale signup draft, the presence of a backend
	// record is enough to prove that account setup already completed. New accounts do not receive
	// that record until onboarding is submitted.
	const { data: userAccount, isLoading: isLoadingAccount } = useUserAccount();
	const hasUserRecord = Boolean(userAccount?.userID);

	// --- resume path ---------------------------------------------------------------------------

	const [resumeSetup, setResumeSetupState] = useState<ResumeSetupState | undefined>(
		() => getPendingSignup()?.resumeSetup,
	);
	// Set once the file is in S3: the questions are behind us and the wait takes over the screen.
	const [waiting, setWaiting] = useState(() => getPendingSignup()?.resumeSetup?.phase === "extract");
	const [resumeFile, setResumeFile] = useState<File | null>(null);
	const [resumeFileError, setResumeFileError] = useState<string | null>(null);

	const {
		abandon: abandonResumeSetup,
		failure: resumeFailure,
		finish: finishResumeSetup,
		resumeFromDsp,
		retry: retryResumeSetup,
		running: resumeRunning,
		start: startResumeSetup,
	} = useResumeSetup({
		onExtracting: (nextResumeSetup) => {
			setResumeSetupState(nextResumeSetup);
			setWaiting(true);
		},
	});

	// The resume path talks to the API before the questionnaire would have created anything, so it
	// needs a signed-in visitor. That is exactly the condition `onValid` already trusts to POST
	// /users itself, so the two answer the same question the same way.
	//
	// This used to read the Authorization cookie directly. It came back false straight after a real
	// Google sign-up — the drop zone never appeared — while every API call on the page authenticated
	// fine, so the cookie was there and the check was wrong about it. Deriving it from the draft
	// keeps one source of truth: if this is wrong, account creation was already wrong too.
	const signedIn = pendingSignup?.method === "google" || Boolean(pendingSignup?.emailVerified);

	// POST /users needs both names and the resume path asks no questions, so a nameless draft — the
	// account-recovery case above — gets the questionnaire, which collects them.
	const canOfferResume = signedIn && !needsName;

	const recovery = reconcileResumeSetup(resumeSetup, hasUserRecord);

	// After a successful POST /users we deliberately do not refetch the account (the redirect below
	// would fire mid-upload), so `hasUserRecord` stays stale within the session. A failure at any
	// stage past the first is the other proof that the record landed.
	const accountExists = hasUserRecord || (resumeFailure !== null && resumeFailure.stage !== "user");

	// Both options sit on the page together until the user commits to one. Touching a field takes
	// the resume away; choosing a file takes the questions away. Neither is a decision the user has
	// to make up front — they just start, and the page gets out of the way.
	const startedTheForm = isDirty;

	const startedTheResume = resumeFile !== null || resumeRunning || resumeFailure !== null || Boolean(resumeSetup);

	const showResume = canOfferResume && !startedTheForm;
	const showQuestions = !startedTheResume;

	const goToProfile = () => {
		finishResumeSetup();
		clearPendingSignup();
		navigate("/profile");
	};

	function uploadResume(file: File) {
		posthog.capture("registration_setup_method_selected", { method: "resume" });

		// Past the first step the record already exists, so pick up at the DSP rather than posting a
		// second user.
		if (accountExists || recovery.action === "needs-file" || recovery.action === "dead-end") {
			resumeFromDsp(file);
		} else if (pendingSignup) {
			startResumeSetup(file, pendingSignup);
		}
	}

	function trackQuestionAnswered(question: string) {
		if (answeredQuestions.current.has(question)) return;
		answeredQuestions.current.add(question);
		posthog.capture("registration_question_answered", { question, step: "onboarding" });
	}

	useEffect(() => {
		if (!pendingSignup) return;
		posthog.capture("registration_onboarding_started", { method: pendingSignup.method });
	}, [pendingSignup]);

	// Rehydrate if the user came back to this step.
	useEffect(() => {
		if (!pendingSignup) return;
		reset({
			firstName: pendingSignup.firstName || "",
			lastName: pendingSignup.lastName || "",
			jobTitle: pendingSignup.jobTitle || "",
			areasOfExpertise: pendingSignup.areasOfExpertise || [],
			recentGraduate: pendingSignup.recentGraduate || "",
			experienceMonth: pendingSignup.experienceMonth || "",
			experienceYear: pendingSignup.experienceYear || "",
			seniorityLevel: pendingSignup.seniorityLevel || "",
			location: pendingSignup.location || "",
		});
	}, [pendingSignup, reset]);

	// Don't flash the questions at someone who is about to be bounced past them.
	if (isLoadingAccount) {
		return null;
	}

	// A resume setup in flight creates the backend record as its *first* step, so the record alone
	// no longer proves that setup finished. While `resumeSetup` is present — at any phase — this
	// redirect would pull the user out of an upload they have not finished. It is cleared only at
	// the one terminal exit.
	if (hasUserRecord && !resumeSetup) {
		// The register form wrote a draft on the way in; it would otherwise re-POST /users later.
		clearPendingSignup();
		return <Navigate to="/profile" replace />;
	}

	if (!pendingSignup) {
		return <Navigate to="/register" replace />;
	}

	// Create the backend record from the collected draft and land on the profile.
	// Used for accounts that are already signed in (Google, or an email account
	// whose link was already followed), so the record exists before they reach a
	// page that requires it.
	function createAccountAndGoToProfile(draft: PendingSignup) {
		if (isCreatingRef.current) return;
		isCreatingRef.current = true;
		setSubmitting(true);

		// Persist the answers before the request so they survive a refresh — or the
		// response interceptor redirecting on an auth failure — and are cleared only
		// once the record exists.
		setPendingSignup(draft);

		const experienceDate =
			draft.experienceMonth && draft.experienceYear
				? { month: monthToNumber(draft.experienceMonth), year: Number(draft.experienceYear) }
				: null;
		const experienceKey = draft.recentGraduate === "Yes" ? "graduationDate" : "professionalSince";

		// Only the keys the backend whitelists — it rejects unknown fields.
		const data = {
			emailAddress: draft.email,
			firstName: draft.firstName,
			lastName: draft.lastName,
			...(draft.phone ? { phoneNumber: draft.phone } : {}),
			jobPreferences: {
				desiredTitle: draft.jobTitle,
				areasOfExpertise: draft.areasOfExpertise || [],
				seniorityLevel: draft.seniorityLevel,
				...(draft.recentGraduate ? { recentGraduate: draft.recentGraduate } : {}),
				...(draft.recentGraduate && experienceDate ? { [experienceKey]: experienceDate } : {}),
				// `country` is a required enum on the Address DTO, so it goes with every city.
				...(draft.location ? { address: { ...splitLocation(draft.location), country: "Tanzania" } } : {}),
			},
		};

		api.createUserAccount(
			data,
			async (response: { data: { jobPreferences?: { desiredTitle?: string } } }) => {
				let saved = response.data;

				// POST /users only applies the body when it actually creates the record. Sent with a
				// token for an account that already exists, it returns that account untouched and
				// reports 200 (users.controller.ts:19) — every answer on this form is dropped, and the
				// user lands on a 0% profile. PUT /users/account is the endpoint that writes
				// preferences, so fall through to it whenever the create was a no-op.
				if (saved?.jobPreferences?.desiredTitle !== data.jobPreferences.desiredTitle) {
					try {
						const updated = await API.put("users/account", data);
						saved = updated.data;
					} catch (error) {
						isCreatingRef.current = false;
						setSubmitting(false);
						toast.error(getErrorMessage(error, "We saved your account but not your preferences. Please try again."));
						return;
					}
				}

				localStorage.setItem("jobPreferences", JSON.stringify(saved));
				clearPendingSignup();
				// Hand /profile the record the server just returned. Invalidating on its own left the
				// refetch racing this navigation, and a read that landed before the write was visible
				// got cached as fresh — the profile we had just filled in rendering as 0%.
				primeUserAccount(saved);
				navigate("/profile");
			},
			(error: { response?: { data?: { message?: string | string[] } } }) => {
				isCreatingRef.current = false;
				setSubmitting(false);
				// class-validator returns `message` as a string[] for validation failures.
				const message = error?.response?.data?.message;
				toast.error(
					(Array.isArray(message) ? message.join(". ") : message) ||
						"We couldn't finish setting up your profile. Please try again.",
				);
			},
		);
	}

	function onValid({
		firstName,
		lastName,
		jobTitle,
		areasOfExpertise,
		recentGraduate,
		experienceMonth,
		experienceYear,
		seniorityLevel,
		location,
	}: OnboardingFormValues) {
		// A second submit that lands before the button re-renders as disabled would otherwise
		// re-run the validation and the "completed" analytics event.
		if (isCreatingRef.current) return;
		if (recentGraduate !== "Yes" && recentGraduate !== "No") return;

		const draft: PendingSignup = {
			...pendingSignup,
			firstName,
			lastName,
			jobTitle,
			areasOfExpertise,
			recentGraduate,
			experienceMonth,
			experienceYear,
			// Asked outright now rather than derived from the date. `deriveSeniorityLevel` remains the
			// fallback for a draft that predates the question.
			seniorityLevel: seniorityLevel || deriveSeniorityLevel(monthToNumber(experienceMonth), Number(experienceYear)),
			location,
		};

		posthog.capture("registration_onboarding_completed", {
			method: draft.method,
			setup_method: "manual",
			recent_graduate: recentGraduate,
			seniority_level: draft.seniorityLevel,
		});

		// An email account is not signed in until its verification link is followed, so it finishes
		// through /creating-profile, which creates the record and shows the "check your email" screen.
		// A Google (or already-verified) account is signed in now: create the record here and go
		// straight to the profile, so there is no step where a signed-in user has no record yet.
		if (draft.method === "google" || draft.emailVerified) {
			createAccountAndGoToProfile(draft);
		} else {
			setPendingSignup(draft);
			navigate("/creating-profile", { state: { method: draft.method } });
		}
	}

	const onInvalid: SubmitErrorHandler<OnboardingFormInput> = (invalidErrors) => {
		if (isCreatingRef.current) return;

		const invalidFields = FIELDS_IN_ORDER.filter(({ name }) => invalidErrors[name]);
		const firstInvalidField = invalidFields[0];
		if (!firstInvalidField) return;

		posthog.capture("registration_onboarding_validation_failed", {
			field: firstInvalidField.name,
			invalid_fields: invalidFields.map(({ name }) => name),
			invalid_field_count: invalidFields.length,
			viewport: isMobileViewport() ? "mobile" : "desktop",
		});

		const validationMessage = invalidErrors[firstInvalidField.name]?.message;
		if (typeof validationMessage === "string") toast.error(validationMessage);

		const field = document.getElementById(firstInvalidField.elementId);
		if (!field) return;

		field.scrollIntoView({ behavior: "smooth", block: "center" });
		// Fields whose id sits on a wrapper — the multi-select — put the caret on the input inside it.
		const focusTarget = field.querySelector("input") ?? field;
		focusTarget.focus({ preventScroll: true });
	};

	if (waiting) {
		return (
			<AuthLayout>
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
					<ExtractionWaiting
						dspId={resumeSetup?.dspId}
						startedAt={resumeSetup?.startedAt ?? Date.now()}
						onDone={() => {
							posthog.capture("registration_onboarding_completed", {
								method: pendingSignup.method,
								setup_method: "resume",
							});
							goToProfile();
						}}
					/>
				</motion.div>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout>
			<div className="py-12">
				{/* Left-aligned, like the register screen before it and every field label below it. */}
				<div className="mb-6 space-y-2">
					<h3 className="font-bold! text-[28px] leading-tight text-[#161616] font-plus-jakarta! mb-2">
						Welcome{pendingSignup.firstName ? `, ${pendingSignup.firstName}` : ""}!
					</h3>
					<p className="text-[#5C5C5C] text-sm">
						{needsName
							? "Let's finish setting up your account, then customise your job recommendations."
							: "Let's customise your job recommendations."}
					</p>
				</div>

				<AnimatePresence initial={false}>
					{showResume ? (
						<motion.div
							key="resume"
							className="overflow-hidden"
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={COLLAPSE}
						>
							<div className="mb-2">
								{recovery.action === "dead-end" ? (
									<p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
										That upload did not finish, and we cannot retry the same copy of the file. Your account is ready, so
										choose a different file, or go to your profile and fill in your details there.
									</p>
								) : null}

								{resumeFailure && recovery.action !== "dead-end" ? (
									<p className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
										{resumeFailure.message}
										{resumeFailure.stage !== "user"
											? " Your account is ready, only the resume still needs to go through."
											: ""}
									</p>
								) : null}

								<FieldLabel htmlFor="uploadFile" className="mb-2 block">
									Upload your resume and we&#39;ll set up your preferences for you
								</FieldLabel>

								<FileUpload
									processDataOfUploadedFile={(uploaded) => setResumeFile(uploaded as File)}
									removeFile={() => {
										setResumeFile(null);
										// Nothing was created while a file merely sat there, so the draft can be tidied
										// up and the questions can come back.
										abandonResumeSetup();
										setResumeSetupState(undefined);
									}}
									fileName={resumeFile?.name || ""}
									fileError={resumeFileError}
									setFileError={setResumeFileError}
									multiple={false}
									supportedFiles={PDF_ONLY}
									disableFileInput={resumeRunning}
								/>

								<AnimatePresence initial={false}>
									{startedTheResume ? (
										<motion.div
											key="upload-actions"
											className="flex flex-col gap-3 overflow-hidden"
											initial={{ opacity: 0, height: 0 }}
											animate={{ opacity: 1, height: "auto" }}
											exit={{ opacity: 0, height: 0 }}
											transition={COLLAPSE}
										>
											<Button
												className="w-full"
												type="button"
												disabled={resumeRunning || (!resumeFile && resumeFailure?.stage !== "s3")}
												onClick={() => {
													if (resumeFailure) {
														retryResumeSetup(resumeFile, pendingSignup);
													} else if (resumeFile) {
														uploadResume(resumeFile);
													}
												}}
											>
												{resumeRunning ? "Uploading your resume…" : resumeFailure ? "Try again" : "Upload and finish"}
											</Button>

											{accountExists && !resumeRunning ? (
												<button
													type="button"
													onClick={goToProfile}
													className="text-sm text-primary-700 underline-offset-4 hover:underline"
												>
													Skip this and go to my profile
												</button>
											) : null}
										</motion.div>
									) : null}
								</AnimatePresence>
							</div>
						</motion.div>
					) : null}
				</AnimatePresence>

				<AnimatePresence initial={false}>
					{showResume && showQuestions ? (
						<motion.div
							key="divider"
							className="overflow-hidden"
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={COLLAPSE}
						>
							<div className="my-6 flex items-center gap-3 overflow-hidden">
								<span className="h-px flex-1 bg-gray-200" />
								{/* gray-400 is #ced4da — under 2:1 on white. gray-700 clears AA at this size. */}
								<span className="text-xs text-gray-700">or answer 5 quick questions</span>
								<span className="h-px flex-1 bg-gray-200" />
							</div>
						</motion.div>
					) : null}
				</AnimatePresence>

				<AnimatePresence initial={false}>
					{showQuestions ? (
						<motion.div
							key="questions"
							className="overflow-hidden"
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={COLLAPSE}
						>
							<form
								noValidate
								onSubmit={submitForm(onValid, onInvalid)}
								className="flex flex-col gap-4 overflow-hidden"
							>
								{needsName ? (
									<div className="grid grid-cols-2 gap-3 mb-4">
										<Field className="gap-1" data-invalid={!!errors.firstName}>
											<FieldLabel htmlFor="onboarding-first-name">First name</FieldLabel>
											<Input
												id="onboarding-first-name"
												type="text"
												placeholder="Enter your first name"
												aria-invalid={!!errors.firstName}
												aria-describedby={errors.firstName ? "onboarding-first-name-error" : undefined}
												{...register("firstName", {
													onBlur: (event) => {
														if (event.target.value.trim()) trackQuestionAnswered("first_name");
													},
												})}
											/>
											<FieldError id="onboarding-first-name-error" errors={[errors.firstName]} />
										</Field>
										<Field className="gap-1" data-invalid={!!errors.lastName}>
											<FieldLabel htmlFor="onboarding-last-name">Last name</FieldLabel>
											<Input
												id="onboarding-last-name"
												type="text"
												placeholder="Enter your last name"
												aria-invalid={!!errors.lastName}
												aria-describedby={errors.lastName ? "onboarding-last-name-error" : undefined}
												{...register("lastName", {
													onBlur: (event) => {
														if (event.target.value.trim()) trackQuestionAnswered("last_name");
													},
												})}
											/>
											<FieldError id="onboarding-last-name-error" errors={[errors.lastName]} />
										</Field>
									</div>
								) : null}

								<Field className="gap-1 mb-4" data-invalid={!!errors.jobTitle}>
									<FieldLabel htmlFor="onboarding-job-title">What&#39;s your dream job title?</FieldLabel>
									<Input
										id="onboarding-job-title"
										type="text"
										placeholder="Enter your desired job title"
										maxLength={MAX_JOB_TITLE_LENGTH}
										aria-invalid={!!errors.jobTitle}
										aria-describedby={errors.jobTitle ? "onboarding-job-title-error" : undefined}
										{...register("jobTitle", {
											onBlur: (event) => {
												if (event.target.value.trim()) trackQuestionAnswered("job_title");
											},
										})}
									/>
									<FieldError id="onboarding-job-title-error" errors={[errors.jobTitle]} />
								</Field>

								<Controller
									name="areasOfExpertise"
									control={control}
									render={({ field }) => (
										<Field className="gap-1 mb-4" data-invalid={!!errors.areasOfExpertise}>
											<FieldLabel htmlFor="onboarding-areas-of-expertise">
												What&#39;s your superpower skill or area of expertise?
											</FieldLabel>
											<SearchableSelect
												id="onboarding-areas-of-expertise"
												name={field.name}
												placeholder="Select all that apply"
												multiple
												search
												options={jobFunctionOptions}
												value={field.value}
												onChange={(selectedOption) => {
													if (field.value.includes(selectedOption)) {
														field.onChange(field.value.filter((item) => item !== selectedOption));
													} else {
														field.onChange([...field.value, selectedOption]);
														trackQuestionAnswered("area_of_expertise");
													}
												}}
												onBlur={field.onBlur}
												aria-invalid={!!errors.areasOfExpertise}
												aria-describedby={errors.areasOfExpertise ? "onboarding-areas-of-expertise-error" : undefined}
											/>
											<FieldError id="onboarding-areas-of-expertise-error" errors={[errors.areasOfExpertise]} />
										</Field>
									)}
								/>

								<Controller
									name="recentGraduate"
									control={control}
									render={({ field }) => (
										<Field className="gap-1 mb-6" data-invalid={!!errors.recentGraduate}>
											<FieldLabel id="onboarding-recent-graduate-label">Are you a recent graduate?</FieldLabel>
											<div
												id="onboarding-recent-graduate"
												tabIndex={-1}
												className="flex gap-3 mt-1 flex-wrap focus:outline-none"
												role="group"
												aria-labelledby="onboarding-recent-graduate-label"
												aria-invalid={!!errors.recentGraduate}
												aria-describedby={errors.recentGraduate ? "onboarding-recent-graduate-error" : undefined}
											>
												{(["Yes", "No"] as YesNo[]).map((answer) => (
													<OptionBadge
														key={answer}
														value={answer}
														selected={field.value === answer}
														onSelect={() => {
															if (answer !== field.value) {
																// The two branches ask about different dates — don't carry one answer into the other.
																setValue("experienceMonth", "");
																setValue("experienceYear", "");
																clearErrors(["experienceMonth", "experienceYear"]);
															}
															field.onChange(answer);
															trackQuestionAnswered("recent_graduate");
														}}
													/>
												))}
											</div>
											<FieldError id="onboarding-recent-graduate-error" errors={[errors.recentGraduate]} />

											{recentGraduate ? (
												<Field className="gap-1 mt-3" data-invalid={!!errors.experienceMonth}>
													<FieldLabel id="onboarding-experience-date-label">
														{recentGraduate === "Yes"
															? "When did you graduate or expect to graduate?"
															: "When did you start your professional career?"}
													</FieldLabel>
													<div className="grid grid-cols-2 gap-3">
														<Controller
															name="experienceMonth"
															control={control}
															render={({ field: monthField }) => (
																<SearchableSelect
																	id="onboarding-experience-month"
																	name={monthField.name}
																	placeholder="Select Month"
																	search
																	options={months}
																	value={monthField.value}
																	onChange={(selectedMonth) => {
																		monthField.onChange(selectedMonth);
																		trackQuestionAnswered(
																			recentGraduate === "Yes" ? "graduation_date" : "career_start",
																		);
																	}}
																	onBlur={monthField.onBlur}
																	aria-invalid={!!errors.experienceMonth}
																	aria-describedby={
																		errors.experienceMonth ? "onboarding-experience-date-error" : undefined
																	}
																	aria-labelledby="onboarding-experience-date-label"
																/>
															)}
														/>
														<Controller
															name="experienceYear"
															control={control}
															render={({ field: yearField }) => (
																<SearchableSelect
																	id="onboarding-experience-year"
																	name={yearField.name}
																	placeholder="Select Year"
																	search
																	options={recentGraduate === "Yes" ? graduationYears() : careerStartYears()}
																	value={yearField.value}
																	onChange={(selectedYear) => {
																		yearField.onChange(selectedYear);
																		trackQuestionAnswered(
																			recentGraduate === "Yes" ? "graduation_date" : "career_start",
																		);
																	}}
																	onBlur={yearField.onBlur}
																	aria-invalid={!!errors.experienceMonth}
																	aria-describedby={
																		errors.experienceMonth ? "onboarding-experience-date-error" : undefined
																	}
																	aria-labelledby="onboarding-experience-date-label"
																/>
															)}
														/>
													</div>
													<FieldError id="onboarding-experience-date-error" errors={[errors.experienceMonth]} />
												</Field>
											) : null}
										</Field>
									)}
								/>

								<Controller
									name="seniorityLevel"
									control={control}
									render={({ field }) => (
										<Field className="gap-1 mb-4" data-invalid={!!errors.seniorityLevel}>
											{/* The stored value is a seniority band, but nobody self-identifies as "Mid-level".
											    Ask the thing they know; the options carry the ranges. */}
											<FieldLabel htmlFor="onboarding-seniority">How many years of experience do you have?</FieldLabel>
											<SearchableSelect
												id="onboarding-seniority"
												name={field.name}
												placeholder="Select your experience"
												search
												options={SENIORITY_OPTIONS}
												value={field.value}
												onChange={(selected) => {
													field.onChange(selected);
													trackQuestionAnswered("seniority_level");
												}}
												onBlur={field.onBlur}
												aria-invalid={!!errors.seniorityLevel}
												aria-describedby={errors.seniorityLevel ? "onboarding-seniority-error" : undefined}
											/>
											<FieldError id="onboarding-seniority-error" errors={[errors.seniorityLevel]} />
										</Field>
									)}
								/>

								<Controller
									name="location"
									control={control}
									render={({ field }) => (
										<Field className="gap-1 mb-6" data-invalid={!!errors.location}>
											<FieldLabel htmlFor="onboarding-location">Where are you located?</FieldLabel>
											<SearchableSelect
												id="onboarding-location"
												name={field.name}
												placeholder="Select your district"
												search
												options={LOCATIONS}
												value={field.value}
												onChange={(selected) => {
													field.onChange(selected);
													trackQuestionAnswered("location");
												}}
												onBlur={field.onBlur}
												aria-invalid={!!errors.location}
												aria-describedby={errors.location ? "onboarding-location-error" : undefined}
											/>
											<FieldError id="onboarding-location-error" errors={[errors.location]} />
										</Field>
									)}
								/>

								<Button className="w-full" type="submit" disabled={submitting}>
									{submitting ? "Setting up your profile…" : "Continue"}
								</Button>
							</form>
						</motion.div>
					) : null}
				</AnimatePresence>

				{/* The upload hid the questions. Offer them back, unless the account already exists — the
				    questionnaire would post a second one. */}
				{!showQuestions && !accountExists && !resumeRunning ? (
					<div className="mt-4 text-center">
						<button
							type="button"
							onClick={() => {
								setResumeFile(null);
								setResumeFileError(null);
								abandonResumeSetup();
								setResumeSetupState(undefined);
							}}
							className="text-sm text-[#5C5C5C] underline-offset-4 hover:underline"
						>
							I would rather answer the questions
						</button>
					</div>
				) : null}

				{/* The form hid the upload. Offer it back while nothing has been created yet. */}
				{!showResume && canOfferResume && !accountExists ? (
					<div className="mt-4 text-center">
						<button
							type="button"
							disabled={submitting}
							onClick={() => {
								posthog.capture("registration_setup_method_changed", { from: "manual", to: "resume" });
								reset();
							}}
							className="text-sm text-primary-700 underline-offset-4 hover:underline disabled:opacity-50"
						>
							Have a resume? Upload it instead and skip these questions.
						</button>
					</div>
				) : null}
			</div>
		</AuthLayout>
	);
}

export default OnboardingScreen;
